import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Store,
  Receipt,
  Heart,
  Coins,
  Package,
  User,
  KeyRound,
  Lock,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useSettings, useUpdateSettings } from "../api";
import {
  useChangeOwnPassword,
  useChangeOwnPin,
  useRemoveOwnPin,
} from "@/features/auth/api";
import { useAuthStore, usePermissions } from "@/stores";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { PageHeader, PageLoader, PageError } from "@/components/common";
import { LoyaltyExample } from "../components/LoyaltyExample";
import type { SettingsMap } from "@/types";

const EMPTY: SettingsMap = {
  shop_name: "",
  shop_address: "",
  shop_phone: "",
  currency: "USD",
  currency_symbol: "$",
  order_number_prefix: "ORD",
  tip_enabled: "false",
  tip_presets: "",
  loyalty_enabled: "false",
  loyalty_points_per_dollar: "0",
  loyalty_redemption_rate: "100",
  loyalty_redemption_value: "1",
  low_stock_alert_enabled: "false",
  receipt_header: "",
  receipt_address: "",
  receipt_phone: "",
  receipt_footer: "",
  receipt_social: "",
};

export function SettingsPage() {
  const { data: settings, isLoading, error, refetch } = useSettings();
  const updateSettings = useUpdateSettings();
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "account";
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const [formData, setFormData] = useState<SettingsMap>(EMPTY);

  useEffect(() => {
    if (settings) {
      setFormData({ ...EMPTY, ...settings });
    }
  }, [settings]);

  const update = <K extends keyof SettingsMap>(
    key: K,
    value: SettingsMap[K],
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync(formData);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save settings",
      );
    }
  };

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load settings" onRetry={refetch} />;

  const canEdit = hasPermission("settings.update");

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="Settings"
        description="Configure your point of sale system"
        actions={
          canEdit && (
            <Button onClick={handleSave} disabled={updateSettings.isPending}>
              {updateSettings.isPending ? "Saving..." : "Save Changes"}
            </Button>
          )
        }
      />
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <div className="w-full overflow-x-auto">
          <TabsList className="w-max">
            <TabsTrigger value="account">
              <User className="mr-2 h-4 w-4" />
              Account
            </TabsTrigger>
            <TabsTrigger value="shop">
              <Store className="mr-2 h-4 w-4" />
              Shop
            </TabsTrigger>
            <TabsTrigger value="receipt">
              <Receipt className="mr-2 h-4 w-4" />
              Receipt
            </TabsTrigger>
            <TabsTrigger value="checkout">
              <Coins className="mr-2 h-4 w-4" />
              Checkout
            </TabsTrigger>
            <TabsTrigger value="loyalty">
              <Heart className="mr-2 h-4 w-4" />
              Loyalty
            </TabsTrigger>
            <TabsTrigger value="inventory">
              <Package className="mr-2 h-4 w-4" />
              Inventory
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="account">
          <AccountSection />
        </TabsContent>
        <TabsContent value="shop">
          <Card>
            <CardHeader>
              <CardTitle>Shop Information</CardTitle>
              <CardDescription>
                Basic information about your coffee shop
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Shop Name" id="shop_name">
                  <Input
                    id="shop_name"
                    value={formData.shop_name}
                    onChange={(e) => update("shop_name", e.target.value)}
                    disabled={!canEdit}
                  />
                </Field>
                <Field label="Phone" id="shop_phone">
                  <Input
                    id="shop_phone"
                    value={formData.shop_phone}
                    onChange={(e) => update("shop_phone", e.target.value)}
                    disabled={!canEdit}
                  />
                </Field>
              </div>
              <Field label="Address" id="shop_address">
                <Textarea
                  id="shop_address"
                  value={formData.shop_address}
                  onChange={(e) => update("shop_address", e.target.value)}
                  disabled={!canEdit}
                  rows={2}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Currency Code" id="currency">
                  <Input
                    id="currency"
                    value={formData.currency}
                    onChange={(e) =>
                      update("currency", e.target.value.toUpperCase())
                    }
                    disabled={!canEdit}
                    maxLength={3}
                  />
                </Field>
                <Field label="Currency Symbol" id="currency_symbol">
                  <Input
                    id="currency_symbol"
                    value={formData.currency_symbol}
                    onChange={(e) => update("currency_symbol", e.target.value)}
                    disabled={!canEdit}
                    maxLength={5}
                  />
                </Field>
              </div>
              <LoyaltyExample
                pointsPerUnit={formData.loyalty_points_per_dollar}
                redemptionRate={formData.loyalty_redemption_rate}
                redemptionValue={formData.loyalty_redemption_value}
                currencySymbol={formData.currency_symbol}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="receipt">
          <Card>
            <CardHeader>
              <CardTitle>Receipt</CardTitle>
              <CardDescription>
                What customers see on their printed receipt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field
                label="Header"
                id="receipt_header"
                hint="Appears at the top of the receipt"
              >
                <Input
                  id="receipt_header"
                  value={formData.receipt_header}
                  onChange={(e) => update("receipt_header", e.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Address" id="receipt_address">
                  <Input
                    id="receipt_address"
                    value={formData.receipt_address}
                    onChange={(e) => update("receipt_address", e.target.value)}
                    disabled={!canEdit}
                  />
                </Field>
                <Field label="Phone" id="receipt_phone">
                  <Input
                    id="receipt_phone"
                    value={formData.receipt_phone}
                    onChange={(e) => update("receipt_phone", e.target.value)}
                    disabled={!canEdit}
                  />
                </Field>
              </div>
              <Field
                label="Social Handle"
                id="receipt_social"
                hint="e.g. @coffeeshop"
              >
                <Input
                  id="receipt_social"
                  value={formData.receipt_social}
                  onChange={(e) => update("receipt_social", e.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Footer Message" id="receipt_footer">
                <Textarea
                  id="receipt_footer"
                  value={formData.receipt_footer}
                  onChange={(e) => update("receipt_footer", e.target.value)}
                  disabled={!canEdit}
                  rows={3}
                  placeholder="Thank you for your visit!"
                />
              </Field>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="checkout">
          <Card>
            <CardHeader>
              <CardTitle>Checkout</CardTitle>
              <CardDescription>Order numbering and tipping</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field
                label="Order Number Prefix"
                id="order_number_prefix"
                hint="Used on receipts, e.g. ORD-0012"
              >
                <Input
                  id="order_number_prefix"
                  value={formData.order_number_prefix}
                  onChange={(e) =>
                    update("order_number_prefix", e.target.value.toUpperCase())
                  }
                  disabled={!canEdit}
                  maxLength={10}
                />
              </Field>
              <ToggleRow
                label="Enable Tipping"
                description="Show a tip prompt during checkout"
                checked={formData.tip_enabled === "true"}
                onCheckedChange={(v) =>
                  update("tip_enabled", v ? "true" : "false")
                }
                disabled={!canEdit}
              />
              <Field
                label="Tip Presets (%)"
                id="tip_presets"
                hint="Comma-separated percentages, e.g. 10,15,18,20"
              >
                <Input
                  id="tip_presets"
                  value={formData.tip_presets}
                  onChange={(e) => update("tip_presets", e.target.value)}
                  disabled={!canEdit || formData.tip_enabled !== "true"}
                />
              </Field>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="loyalty">
          <Card>
            <CardHeader>
              <CardTitle>Loyalty Program</CardTitle>
              <CardDescription>
                Configure customer loyalty rewards
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ToggleRow
                label="Enable Loyalty"
                description="Earn and redeem points on customer orders"
                checked={formData.loyalty_enabled === "true"}
                onCheckedChange={(v) =>
                  update("loyalty_enabled", v ? "true" : "false")
                }
                disabled={!canEdit}
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Points per Dollar"
                  id="loyalty_points_per_dollar"
                  hint={`Points earned per ${formData.currency_symbol}1 of order total`}
                >
                  <Input
                    id="loyalty_points_per_dollar"
                    type="number"
                    value={formData.loyalty_points_per_dollar}
                    onChange={(e) =>
                      update("loyalty_points_per_dollar", e.target.value)
                    }
                    disabled={!canEdit || formData.loyalty_enabled !== "true"}
                    placeholder="e.g., 10"
                  />
                </Field>
                <Field
                  label="Redemption Rate"
                  id="loyalty_redemption_rate"
                  hint="Points a customer spends to redeem one unit of value"
                >
                  <Input
                    id="loyalty_redemption_rate"
                    type="number"
                    value={formData.loyalty_redemption_rate}
                    onChange={(e) =>
                      update("loyalty_redemption_rate", e.target.value)
                    }
                    disabled={!canEdit || formData.loyalty_enabled !== "true"}
                    placeholder="e.g., 100"
                  />
                </Field>
                <Field
                  label="Redemption Value"
                  id="loyalty_redemption_value"
                  hint={`How much one redemption unit takes off, in ${formData.currency_symbol}`}
                >
                  <Input
                    id="loyalty_redemption_value"
                    type="number"
                    step="0.01"
                    value={formData.loyalty_redemption_value}
                    onChange={(e) =>
                      update("loyalty_redemption_value", e.target.value)
                    }
                    disabled={!canEdit || formData.loyalty_enabled !== "true"}
                    placeholder="e.g., 1.00"
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="inventory">
          <Card>
            <CardHeader>
              <CardTitle>Inventory</CardTitle>
              <CardDescription>Alerts and stock behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ToggleRow
                label="Low Stock Alerts"
                description="Warn when ingredients fall below their threshold"
                checked={formData.low_stock_alert_enabled === "true"}
                onCheckedChange={(v) =>
                  update("low_stock_alert_enabled", v ? "true" : "false")
                }
                disabled={!canEdit}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ id, label, hint, children }: FieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
}: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between rounded-md border p-4">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}

function AccountSection() {
  const user = useAuthStore((state) => state.user);
  const changePassword = useChangeOwnPassword();
  const changePin = useChangeOwnPin();
  const removePin = useRemoveOwnPin();

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [pinForm, setPinForm] = useState({
    currentPassword: "",
    newPin: "",
    confirmPin: "",
  });

  const [removePassword, setRemovePassword] = useState("");

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Passwords do not match");

      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");

      return;
    }

    try {
      await changePassword.mutateAsync({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success("Password changed successfully");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to change password",
      );
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (pinForm.newPin !== pinForm.confirmPin) {
      toast.error("PINs do not match");

      return;
    }

    if (!/^\d{4,6}$/.test(pinForm.newPin)) {
      toast.error("PIN must be 4-6 digits");

      return;
    }

    try {
      await changePin.mutateAsync({
        currentPassword: pinForm.currentPassword,
        newPin: pinForm.newPin,
      });
      toast.success("PIN updated successfully");
      setPinForm({ currentPassword: "", newPin: "", confirmPin: "" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update PIN");
    }
  };

  const handleRemovePin = async () => {
    if (!removePassword) {
      toast.error("Please enter your password");

      return;
    }

    try {
      await removePin.mutateAsync({ currentPassword: removePassword });
      toast.success("PIN removed successfully");
      setRemovePassword("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove PIN");
    }
  };

  const hasPin = user?.hasPin ?? false;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <p className="font-medium">
                {user?.firstName} {user?.lastName}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Username</Label>
              <p className="font-medium">{user?.username}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <p className="font-medium capitalize">{user?.role?.name}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    currentPassword: e.target.value,
                  }))
                }
                autoComplete="current-password"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  autoComplete="new-password"
                />
              </div>
            </div>
            <Button type="submit" disabled={changePassword.isPending}>
              {changePassword.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            PIN Login
          </CardTitle>
          <CardDescription>
            Set up a 4-6 digit PIN for quick login
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 rounded-md border p-3">
            {hasPin ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium">PIN is configured</span>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-muted-foreground" />
                <span className="text-muted-foreground">No PIN configured</span>
              </>
            )}
          </div>
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin-current-password">Current Password</Label>
              <Input
                id="pin-current-password"
                type="password"
                value={pinForm.currentPassword}
                onChange={(e) =>
                  setPinForm((prev) => ({
                    ...prev,
                    currentPassword: e.target.value,
                  }))
                }
                autoComplete="current-password"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-pin">{hasPin ? "New PIN" : "PIN"}</Label>
                <Input
                  id="new-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="4-6 digits"
                  value={pinForm.newPin}
                  onChange={(e) =>
                    setPinForm((prev) => ({
                      ...prev,
                      newPin: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pin">Confirm PIN</Label>
                <Input
                  id="confirm-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="4-6 digits"
                  value={pinForm.confirmPin}
                  onChange={(e) =>
                    setPinForm((prev) => ({
                      ...prev,
                      confirmPin: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  autoComplete="off"
                />
              </div>
            </div>
            <Button type="submit" disabled={changePin.isPending}>
              {changePin.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {hasPin ? "Change PIN" : "Set PIN"}
            </Button>
          </form>
          {hasPin && (
            <div className="border-t pt-4">
              <p className="mb-3 text-sm text-muted-foreground">
                Remove your PIN to disable PIN login
              </p>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="Enter password to remove PIN"
                  value={removePassword}
                  onChange={(e) => setRemovePassword(e.target.value)}
                  className="max-w-xs"
                />
                <Button
                  variant="outline"
                  onClick={handleRemovePin}
                  disabled={removePin.isPending}
                >
                  {removePin.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Remove PIN
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
